import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';

import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { TargetingSearchListApi } from './model/targeting-search-list.api.model';
import { TargetingSearchApi, TargetingQueryApi } from './model/targeting-search.api.model';
import { TargetingSearchResponseApiModel } from './model/targeting-search-response.api.model';
import { SortOrderEnum } from '../shared/enums/sort-order.enum';
import { NeptuneApi } from '../genericApi/neptune.api';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';

import { DisplayGroupPatchModel } from '../displayGroups/models/display-group-patch.api.model';
import { MultiBaseAbstractApi } from '../genericApi/multi-base-abstract.api';

@Injectable()
export class TargetingSearchesApiService extends MultiBaseAbstractApi<TargetingSearchApi, TargetingSearchListApi> {

    private locale: string = 'en-US';

    constructor(
        private neptuneApi: NeptuneApi,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super();
        this.currentUserStateService.getDataReferenceLanguageTag().pipe(
            filter(x => !!x),
            take(1)
        ).subscribe(languageTag => {
            this.locale = languageTag;
        });
    }


    getList(): Observable<TargetingSearchListApi[]> {
        const slug: string = '/targetingsearches/';

        return this.neptuneApi.api<TargetingSearchListApi[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getById(id: number): Observable<TargetingSearchApi> {
        const slug: string = `/targetingsearches/${id}`;

        return this.neptuneApi.api<TargetingSearchApi>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    create(search: TargetingSearchApi): Observable<number> {
        const slug: string = '/targetingsearches/';

        return this.neptuneApi.api<number>(
            slug,
            search,
            HttpVerbsEnum.POST
        );
    }

    // @TODO - endpoint should return id
    update(search: TargetingSearchApi): Observable<number> {
        const slug: string = `/targetingsearches/${search.id}`;

        return this.neptuneApi.api<void>(
            slug,
            search,
            HttpVerbsEnum.PUT
        ).pipe(
            map(() => search.id)
        );
    }

    // @TODO - endpoint should return id
    deleteById(id: number): Observable<number> {
        const slug: string = `/targetingsearches/${id}`;

        return this.neptuneApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        ).pipe(
            map(() => id)
        );
    }

    performSearch(
        search: TargetingQueryApi,
        start: number,
        rows: number,
        sortOrder?: SortOrderEnum,
        sortField?: string
    ): Observable<TargetingSearchResponseApiModel> {
        const slug: string = '/targetingsearches/search';

        const params = {
            rows: rows,
            start: start,
            sortBy: sortField,
            sortOrder: sortOrder,
            locale: this.locale
        } as any;

        if (!sortOrder) {
            delete params.sortOrder;
        }
        if (!sortField) {
            delete params.sortBy;
        }
        return this.neptuneApi.api<TargetingSearchResponseApiModel>(
            slug,
            search,
            HttpVerbsEnum.POST,
            params
        );
    }

    performMapSearch(search: TargetingQueryApi): Observable<TargetingSearchResponseApiModel> {
        const slug: string = '/targetingsearches/search/map';

        return this.neptuneApi.api<TargetingSearchResponseApiModel>(
            slug,
            search,
            HttpVerbsEnum.POST
        );
    }

    performStaffSearch(
        outletId: string,
        search: TargetingQueryApi,
        start: number,
        rows: number,
        sortOrder: SortOrderEnum,
        sortField: string
    ): Observable<TargetingSearchResponseApiModel> {
        const slug: string = `/targetingsearches/search/${outletId}/staff`;

        return this.neptuneApi.api<TargetingSearchResponseApiModel>(
            slug,
            search,
            HttpVerbsEnum.POST,
            {
                rows: rows,
                start: start,
                sortBy: sortField,
                sortOrder: sortOrder,
                locale: this.locale

            }
        );
    }

    getSearchCount(searchId: number, searchFilter: TargetingQueryApi): Observable<number> {
        const slug: string = `/targetingsearches/${searchId}/count`;

        return this.neptuneApi.api<number>(
            slug,
            searchFilter,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Adds and/or removes targeting searches to/from provided group
     * @param addIds Searches to add to group
     * @param removeIds Searches to remove from group
     * @param groupId
     */
    updateDisplayGroup(addIds: number[], removeIds: number[], groupId: number): Observable<void> {
        const slug: string = `/targetingsearches/displaygroups/${groupId}`;
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Removes group ID from all provided targeting searches
     * @param removeIds Searches to remove from groups
     */
    removeDisplayGroup(removeIds: number[]): Observable<void> {
        const slug: string = '/targetingsearches/displaygroups';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: [],
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Adds/removes targeting searches from favourites
     * @param addIds Searchs to add to favourites
     * @param removeIds Searches to remove from favourites
     * @returns
     */
    updateFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        const slug: string = '/targetingsearches/displaygroups/favorites';
        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };
        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    public removeExpiryOfSearch (searchId: number): Observable<void> {
        const slug: string = `/targetingsearches/${searchId}/removeExpiry`;
        return this.neptuneApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.PATCH
        );
    }
}
